import { API_BASE_URL as baseUri } from "config/env";
import { AuthHeader } from "services/authHeader";
import { FilteredDropDownRequest, FilteredDropDownResponse } from "shared/interfaces/core-interfaces";
import { NotificationTypes } from "shared/interfaces/status-notifications-interfaces";
import { showNotification } from "shared/slices/statusNotificationsSlice";
import { createAppAsyncThunk } from "shared/state/createAppAsyncThunk";
import { rejectFailedResponse, rejectNetworkError } from "shared/state/rejectFailedRequest";
import {
    CreateTenantModel,
    DeleteExternalAccessRequest,
    DropdownTenantList,
    ExternalAccessRequest,
    ExternalAccessResponse,
    SaveTenantProjectsRequest,
    SaveTenantProjectsResponse,
    TenantModel,
    TenantProjectAccess,
} from "./tenant-interfaces";
import { GetTenantForListResponse, TenantDTO } from "./tenantDtos";

// Thunks for the tenant itself. The ones whose result a screen keeps (the lists and the overview)
// have reducers in tenantSlice.ts; the others are read with unwrap() by the component that needs
// the result, and are not stored.

interface TenantIdResponse {
    tenantId: string;
}

// --- Stored: the lists and the overview ---

export const GetActiveTenants = createAppAsyncThunk<GetTenantForListResponse[], void>(
    "Slice/TenantSlice/GetActiveTenants",
    async (_, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetch(baseUri + "api/tenant/getAllActive", { method: "GET", headers: AuthHeader() });
            if (response.status === 200) {
                const responseModel = await response.json() as GetTenantForListResponse[];
                return responseModel;
            }
            else {
                return rejectFailedResponse(response, { dispatch, rejectWithValue });
            }
        }
        catch {
            return rejectNetworkError({ dispatch, rejectWithValue });
        }
    }
);

export const GetInactiveTenants = createAppAsyncThunk<GetTenantForListResponse[], void>(
    "Slice/TenantSlice/GetInactiveTenants",
    async (_, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetch(baseUri + "api/tenant/getallinactive", { method: "GET", headers: AuthHeader() });
            if (response.status === 200) {
                const responseModel = await response.json() as GetTenantForListResponse[];
                return responseModel;
            }
            else {
                return rejectFailedResponse(response, { dispatch, rejectWithValue });
            }
        }
        catch {
            return rejectNetworkError({ dispatch, rejectWithValue });
        }
    }
);

/** The tenant page's data. No toast on failure: the page shows the error itself. */
export const GetTenantOverview = createAppAsyncThunk<TenantDTO, string>(
    "Slice/TenantSlice/GetTenantOverview",
    async (tenantId, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetch(baseUri + "api/tenant/get-overview?tenantId=" + encodeURIComponent(tenantId), { method: "GET", headers: AuthHeader() });
            if (response.status === 200) {
                const responseModel = await response.json() as TenantDTO;
                return responseModel;
            }
            else {
                return rejectFailedResponse(response, { dispatch, rejectWithValue }, { notify: false });
            }
        }
        catch {
            return rejectNetworkError({ dispatch, rejectWithValue }, { notify: false });
        }
    }
);

export const DeactivateTenant = createAppAsyncThunk<TenantIdResponse, string>(
    "Slice/TenantSlice/DeactivateTenant",
    async (tenantId, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetch(baseUri + "api/tenant/deactivate?tenantId=" + encodeURIComponent(tenantId), { method: "PUT", headers: AuthHeader() });
            if (response.status === 200) {
                const responseModel = await response.json() as TenantIdResponse;
                dispatch(showNotification({ message: "The tenant is successfully deactivated.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                return rejectFailedResponse(response, { dispatch, rejectWithValue });
            }
        }
        catch {
            return rejectNetworkError({ dispatch, rejectWithValue });
        }
    }
);

export const ReactivateTenant = createAppAsyncThunk<TenantIdResponse, string>(
    "Slice/TenantSlice/ReactivateTenant",
    async (tenantId, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetch(baseUri + "api/tenant/reactivate?tenantId=" + encodeURIComponent(tenantId), { method: "PUT", headers: AuthHeader() });
            if (response.status === 200) {
                const responseModel = await response.json() as TenantIdResponse;
                dispatch(showNotification({ message: "The tenant is successfully reactivated.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                return rejectFailedResponse(response, { dispatch, rejectWithValue });
            }
        }
        catch {
            return rejectNetworkError({ dispatch, rejectWithValue });
        }
    }
);

// --- Not stored: read with unwrap() ---

/** The full tenant for the edit form. Unlike the overview it has the applicant tracking email. */
export const GetTenant = createAppAsyncThunk<TenantModel, string>(
    "Slice/TenantSlice/GetTenant",
    async (tenantId, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetch(baseUri + "api/tenant/get?tenantId=" + encodeURIComponent(tenantId), { method: "GET", headers: AuthHeader() });
            if (response.status === 200) {
                const responseModel = await response.json() as TenantModel;
                return responseModel;
            }
            else {
                return rejectFailedResponse(response, { dispatch, rejectWithValue });
            }
        }
        catch {
            return rejectNetworkError({ dispatch, rejectWithValue });
        }
    }
);

/** Countries, currencies, languages, time zones, formats and employees for the tenant form. */
export const GetTenantSelectLists = createAppAsyncThunk<DropdownTenantList, void>(
    "Slice/TenantSlice/GetTenantSelectLists",
    async (_, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetch(baseUri + "api/tenant/getSelectLists", { method: "GET", headers: AuthHeader() });
            if (response.status === 200) {
                const responseModel = await response.json() as DropdownTenantList;
                return responseModel;
            }
            else {
                return rejectFailedResponse(response, { dispatch, rejectWithValue });
            }
        }
        catch {
            return rejectNetworkError({ dispatch, rejectWithValue });
        }
    }
);

export const CreateTenant = createAppAsyncThunk<TenantIdResponse, CreateTenantModel>(
    "Slice/TenantSlice/CreateTenant",
    async (request, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetch(baseUri + "api/tenant/create", { method: "POST", headers: AuthHeader(), body: JSON.stringify(request) });
            if (response.status === 200) {
                const responseModel = await response.json() as TenantIdResponse;
                dispatch(showNotification({ message: "The tenant is successfully created.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                return rejectFailedResponse(response, { dispatch, rejectWithValue });
            }
        }
        catch {
            return rejectNetworkError({ dispatch, rejectWithValue });
        }
    }
);

export const UpdateTenant = createAppAsyncThunk<TenantIdResponse, TenantModel>(
    "Slice/TenantSlice/UpdateTenant",
    async (request, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetch(baseUri + "api/tenant/update", { method: "PUT", headers: AuthHeader(), body: JSON.stringify(request) });
            if (response.status === 200) {
                const responseModel = await response.json() as TenantIdResponse;
                dispatch(showNotification({ message: "The tenant is successfully updated.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                return rejectFailedResponse(response, { dispatch, rejectWithValue });
            }
        }
        catch {
            return rejectNetworkError({ dispatch, rejectWithValue });
        }
    }
);

export const GetTenantProjects = createAppAsyncThunk<TenantProjectAccess[], string>(
    "Slice/TenantSlice/GetTenantProjects",
    async (tenantId, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetch(baseUri + "api/tenant/get-projects?tenantId=" + encodeURIComponent(tenantId), { method: "GET", headers: AuthHeader() });
            if (response.status === 200) {
                const responseModel = await response.json() as TenantProjectAccess[];
                return responseModel;
            }
            else {
                return rejectFailedResponse(response, { dispatch, rejectWithValue });
            }
        }
        catch {
            return rejectNetworkError({ dispatch, rejectWithValue });
        }
    }
);

export const SaveTenantProjects = createAppAsyncThunk<SaveTenantProjectsResponse, SaveTenantProjectsRequest>(
    "Slice/TenantSlice/SaveTenantProjects",
    async (request, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetch(baseUri + "api/tenant/save-projects", { method: "PUT", headers: AuthHeader(), body: JSON.stringify(request) });
            if (response.status === 200) {
                const responseModel = await response.json() as SaveTenantProjectsResponse;
                dispatch(showNotification({ message: "The profit/cost centers are successfully saved.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                return rejectFailedResponse(response, { dispatch, rejectWithValue });
            }
        }
        catch {
            return rejectNetworkError({ dispatch, rejectWithValue });
        }
    }
);

export const GetExternalAccess = createAppAsyncThunk<ExternalAccessResponse[], string>(
    "Slice/TenantSlice/GetExternalAccess",
    async (tenantId, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetch(baseUri + "api/tenant/getExternalAccess?tenantId=" + encodeURIComponent(tenantId), { method: "GET", headers: AuthHeader() });
            if (response.status === 200) {
                const responseModel = await response.json() as ExternalAccessResponse[];
                return responseModel;
            }
            else {
                return rejectFailedResponse(response, { dispatch, rejectWithValue });
            }
        }
        catch {
            return rejectNetworkError({ dispatch, rejectWithValue });
        }
    }
);

export const AddExternalAccess = createAppAsyncThunk<ExternalAccessResponse[], ExternalAccessRequest>(
    "Slice/TenantSlice/AddExternalAccess",
    async (request, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetch(baseUri + "api/tenant/addExternalAccess", { method: "POST", headers: AuthHeader(), body: JSON.stringify(request) });
            if (response.status === 200) {
                const responseModel = await response.json() as ExternalAccessResponse[];
                dispatch(showNotification({ message: "The external user access is successfully created.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                return rejectFailedResponse(response, { dispatch, rejectWithValue });
            }
        }
        catch {
            return rejectNetworkError({ dispatch, rejectWithValue });
        }
    }
);

export const UpdateExternalAccess = createAppAsyncThunk<ExternalAccessResponse[], ExternalAccessRequest>(
    "Slice/TenantSlice/UpdateExternalAccess",
    async (request, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetch(baseUri + "api/tenant/updateExternalAccess", { method: "PUT", headers: AuthHeader(), body: JSON.stringify(request) });
            if (response.status === 200) {
                const responseModel = await response.json() as ExternalAccessResponse[];
                dispatch(showNotification({ message: "The external user access is successfully updated.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                return rejectFailedResponse(response, { dispatch, rejectWithValue });
            }
        }
        catch {
            return rejectNetworkError({ dispatch, rejectWithValue });
        }
    }
);

export const DeleteExternalAccess = createAppAsyncThunk<ExternalAccessResponse, DeleteExternalAccessRequest>(
    "Slice/TenantSlice/DeleteExternalAccess",
    async (request, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetch(baseUri + "api/tenant/deleteExternalAccess", { method: "PUT", headers: AuthHeader(), body: JSON.stringify(request) });
            if (response.status === 200) {
                const responseModel = await response.json() as ExternalAccessResponse;
                dispatch(showNotification({ message: "The external user access is successfully deleted.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                return rejectFailedResponse(response, { dispatch, rejectWithValue });
            }
        }
        catch {
            return rejectNetworkError({ dispatch, rejectWithValue });
        }
    }
);

export const GetContactsForDropdown = createAppAsyncThunk<FilteredDropDownResponse, FilteredDropDownRequest>(
    "Slice/TenantSlice/GetContactsForDropdown",
    async (request, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetch(baseUri + "api/contacts/drop-down", { method: "POST", headers: AuthHeader(), body: JSON.stringify(request) });
            if (response.status === 200) {
                const responseModel = await response.json() as FilteredDropDownResponse;
                return responseModel;
            }
            else {
                return rejectFailedResponse(response, { dispatch, rejectWithValue });
            }
        }
        catch {
            return rejectNetworkError({ dispatch, rejectWithValue });
        }
    }
);
